import type {
  LocationQueryRaw,
  RouteLocationNormalized,
  RouteLocationRaw,
  RouteParamsRaw,
  Router,
} from 'vue-router'
import { PageMeta } from '@/@vxapp/@Enums/PageMeta'

export const AUTH_RETURN_ROUTE_VERSION = 2
// Matches the API's absolute session lifetime; restoring is limited to the same owner.
export const AUTH_RETURN_ROUTE_MAX_AGE_MS = 24 * 60 * 60 * 1000
export const AUTH_RETURN_ROUTE_CLOCK_SKEW_MS = 60 * 1000
const INTERNAL_ROUTE_ORIGIN = 'https://auth-return-route.invalid'

const authLoopRouteNames = new Set(Object.values(PageMeta.Auth).map(authRoute => authRoute.name))

/** The tenant and user a return route belongs to; `null` for guest/lobby captures. */
export interface AuthReturnRouteOwner {
  apiGUID: string
  userGUID: string
}

export interface AuthReturnRoutePayload {
  version: typeof AUTH_RETURN_ROUTE_VERSION
  capturedAt: number
  fullPath: string
  name: string | null
  params: RouteParamsRaw
  query: LocationQueryRaw
  hash: string
  subject: string | null
  owner: AuthReturnRouteOwner | null
}

interface CaptureAuthReturnRouteOptions {
  owner: AuthReturnRouteOwner | null
  capturedAt?: number
}

interface ResolveAuthReturnRouteOptions {
  owner?: AuthReturnRouteOwner | null
  maxAgeMs?: number
  now?: number
}

interface AuthOwnerSource {
  api?: unknown
  userData?: { userGUID?: unknown } | null
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every(item => typeof item === 'string')

const isRouteParams = (value: unknown): value is RouteParamsRaw =>
  isRecord(value) &&
  Object.values(value).every(item => typeof item === 'string' || isStringArray(item))

const isQueryValue = (value: unknown): boolean =>
  typeof value === 'string' ||
  value === null ||
  (Array.isArray(value) && value.every(item => typeof item === 'string' || item === null))

const isRouteQuery = (value: unknown): value is LocationQueryRaw =>
  isRecord(value) && Object.values(value).every(isQueryValue)

const cloneRouteParams = (params: RouteLocationNormalized['params']): RouteParamsRaw =>
  Object.fromEntries(
    Object.entries(params).map(([key, value]) => [key, Array.isArray(value) ? [...value] : value])
  )

const cloneRouteQuery = (query: RouteLocationNormalized['query']): LocationQueryRaw =>
  Object.fromEntries(
    Object.entries(query).map(([key, value]) => [key, Array.isArray(value) ? [...value] : value])
  )

const getInternalUrl = (fullPath: string): URL | null => {
  if (!fullPath.startsWith('/') || fullPath.startsWith('//')) {
    return null
  }

  try {
    const url = new URL(fullPath, INTERNAL_ROUTE_ORIGIN)
    const normalizedFullPath = `${url.pathname}${url.search}${url.hash}`

    if (url.origin !== INTERNAL_ROUTE_ORIGIN || normalizedFullPath !== fullPath) {
      return null
    }

    return url
  } catch {
    return null
  }
}

const isOwner = (value: unknown): value is AuthReturnRouteOwner =>
  isRecord(value) &&
  typeof value.apiGUID === 'string' &&
  value.apiGUID !== '' &&
  typeof value.userGUID === 'string' &&
  value.userGUID !== ''

/** Reads the signed-in owner from the auth store, or `null` when either identity is missing. */
export function authReturnRouteOwner(authStore: AuthOwnerSource): AuthReturnRouteOwner | null {
  const owner = { apiGUID: authStore.api, userGUID: authStore.userData?.userGUID }

  return isOwner(owner) ? owner : null
}

const isSameOwner = (
  saved: AuthReturnRouteOwner | null,
  current: AuthReturnRouteOwner | null | undefined
): boolean =>
  saved === null ||
  (current != null && saved.apiGUID === current.apiGUID && saved.userGUID === current.userGUID)

const isAuthLoopName = (name: unknown): boolean =>
  typeof name === 'string' && authLoopRouteNames.has(name)

const isValidPayload = (
  value: unknown,
  options: Required<Omit<ResolveAuthReturnRouteOptions, 'owner'>>
): value is AuthReturnRoutePayload => {
  if (!isRecord(value)) {
    return false
  }

  const capturedAt = value.capturedAt
  const fullPath = value.fullPath
  const name = value.name
  const hash = value.hash
  const subject = value.subject

  if (
    value.version !== AUTH_RETURN_ROUTE_VERSION ||
    typeof capturedAt !== 'number' ||
    !Number.isFinite(capturedAt) ||
    capturedAt > options.now + AUTH_RETURN_ROUTE_CLOCK_SKEW_MS ||
    options.now - capturedAt > options.maxAgeMs ||
    typeof fullPath !== 'string' ||
    (name !== null && typeof name !== 'string') ||
    typeof hash !== 'string' ||
    (hash !== '' && !hash.startsWith('#')) ||
    (subject !== null && typeof subject !== 'string') ||
    !isRouteParams(value.params) ||
    !isRouteQuery(value.query) ||
    (value.owner !== null && !isOwner(value.owner)) ||
    isAuthLoopName(name)
  ) {
    return false
  }

  const internalUrl = getInternalUrl(fullPath)

  return internalUrl !== null && internalUrl.hash === hash
}

const isResolvedRouteAllowed = (
  resolvedRoute: ReturnType<Router['resolve']>,
  payload: AuthReturnRoutePayload
): boolean => {
  if (!resolvedRoute.matched.length || isAuthLoopName(resolvedRoute.name)) {
    return false
  }

  const resolvedSubject = resolvedRoute.meta.subject

  return payload.subject === null || resolvedSubject === payload.subject
}

export function captureAuthReturnRoute(
  route: RouteLocationNormalized,
  { owner, capturedAt = Date.now() }: CaptureAuthReturnRouteOptions
): AuthReturnRoutePayload | null {
  const name = typeof route.name === 'string' ? route.name : null
  const subject = typeof route.meta.subject === 'string' ? route.meta.subject : null

  if (!getInternalUrl(route.fullPath) || isAuthLoopName(name)) {
    return null
  }

  return {
    version: AUTH_RETURN_ROUTE_VERSION,
    capturedAt,
    fullPath: route.fullPath,
    name,
    params: cloneRouteParams(route.params),
    query: cloneRouteQuery(route.query),
    hash: route.hash,
    subject,
    owner: isOwner(owner) ? { apiGUID: owner.apiGUID, userGUID: owner.userGUID } : null,
  }
}

export function resolveAuthReturnRoute(
  router: Router,
  value: unknown,
  options: ResolveAuthReturnRouteOptions = {}
): RouteLocationRaw | null {
  const resolvedOptions = {
    maxAgeMs: options.maxAgeMs ?? AUTH_RETURN_ROUTE_MAX_AGE_MS,
    now: options.now ?? Date.now(),
  }

  if (!isValidPayload(value, resolvedOptions) || !isSameOwner(value.owner, options.owner)) {
    return null
  }

  if (value.name) {
    const namedLocation = {
      name: value.name,
      params: value.params,
      query: value.query,
      hash: value.hash,
    } as RouteLocationRaw

    try {
      const resolvedNamedRoute = router.resolve(namedLocation)

      if (
        resolvedNamedRoute.fullPath === value.fullPath &&
        isResolvedRouteAllowed(resolvedNamedRoute, value)
      ) {
        return namedLocation
      }
    } catch {
      // The durable fullPath below supports renamed and unnamed routes.
    }
  }

  try {
    const resolvedFullPath = router.resolve(value.fullPath)

    if (
      resolvedFullPath.fullPath === value.fullPath &&
      isResolvedRouteAllowed(resolvedFullPath, value)
    ) {
      return value.fullPath
    }
  } catch {
    return null
  }

  return null
}
